import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
    Image,
    Upload,
    Play,
    Trash2,
    FileText,
    CheckCircle2,
    Clock,
    Loader2,
    AlertTriangle,
    Coins,
    History as HistoryIcon,
    Download,
    RefreshCw,
    Sparkles,
    Eye
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface BulkPhotoJob {
    id: string;
    model_name: string;
    ratio: string;
    status: string;
    total_items: number;
    completed_items: number;
    failed_items: number;
    created_at: string;
}

interface PhotoLog {
    id: string;
    prompt: string;
    model: string;
    result_url: string | null;
    status: string;
    created_at: string;
    credits_used: number;
}

const PhotoBulk = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { userCredits } = useGeneration();
    const { toast } = useToast();

    const [prompts, setPrompts] = useState<string[]>([]);
    const [pasteText, setPasteText] = useState("");
    const [selectedPhotoModel, setSelectedPhotoModel] = useState("seedream");
    const [selectedRatio, setSelectedRatio] = useState("9:16");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recentJobs, setRecentJobs] = useState<BulkPhotoJob[]>([]);
    const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
    const [recentPhotos, setRecentPhotos] = useState<PhotoLog[]>([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const photoModels = [
        { id: "gpt-image", name: "GPT Image", credits: 4 },
        { id: "seedream", name: "Seedream 4.5", credits: 6.5 },
        { id: "nanobana", name: "Nano Banana", credits: 4 },
        { id: "nanobana-pro", name: "Nano Banana Pro", credits: 18 },
    ];

    const aspectRatioOptions = [
        { value: "1:1", label: t("สี่เหลี่ยมจตุรัส", "Square") + " (1:1)" },
        { value: "9:16", label: t("แนวตั้ง", "Portrait") + " (9:16)" },
        { value: "16:9", label: t("แนวนอน", "Landscape") + " (16:9)" },
        { value: "4:3", label: "4:3" },
        { value: "3:4", label: "3:4" },
    ];

    useEffect(() => {
        if (user) {
            fetchRecentJobs();
            fetchRecentPhotos();

            // Disabled realtime subscription to reduce Disk IO
            // Users can manually refresh using the refresh button
            // Previously: subscribed to bulk_photo_jobs and generation_logs changes
        }
    }, [user]);

    const fetchRecentJobs = async () => {
        if (!user) return;
        const { data, error } = await supabase
            .from("bulk_photo_jobs" as any)
            .select("*")
            .order("created_at", { ascending: false })
            .limit(10);

        if (!error && data) {
            setRecentJobs(data as any);
        }
    };

    const fetchRecentPhotos = async () => {
        if (!user) return;
        const { data, error } = await supabase
            .from("generation_logs")
            .select("id, prompt, model, result_url, status, created_at, credits_used")
            .eq("user_id", user.id)
            .eq("service_type", "photo")
            .eq("status", "completed")
            .not("result_url", "is", null)
            .order("created_at", { ascending: false })
            .limit(12);

        if (!error && data) {
            setRecentPhotos(data as PhotoLog[]);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            let extractedPrompts: string[] = [];

            if (file.name.toLowerCase().endsWith(".csv")) {
                // CSV: Take only the first column, skip header if it looks like a header
                const lines = content.split("\n").map(line => line.trim()).filter(line => line.length > 0);
                extractedPrompts = lines.map(line => {
                    // Handle CSV with commas - take first column only
                    if (line.startsWith('"')) {
                        const endQuote = line.indexOf('"', 1);
                        if (endQuote > 0) {
                            return line.substring(1, endQuote).trim();
                        }
                    }
                    const commaIndex = line.indexOf(',');
                    return commaIndex > 0 ? line.substring(0, commaIndex).trim() : line.trim();
                }).filter(p => p.length > 0);

                // Skip first line if it looks like a header
                if (extractedPrompts.length > 1) {
                    const firstLine = extractedPrompts[0].toLowerCase();
                    if (firstLine === "prompt" || firstLine === "prompts" || firstLine === "text" || firstLine.length < 5) {
                        extractedPrompts = extractedPrompts.slice(1);
                    }
                }
            } else {
                // TXT: One prompt per line
                extractedPrompts = content
                    .split("\n")
                    .map(line => line.trim())
                    .filter(line => line.length > 0);
            }

            // Remove duplicates
            extractedPrompts = [...new Set(extractedPrompts)];

            setPrompts(extractedPrompts);
            setPasteText(extractedPrompts.join("\n"));

            toast({
                title: t("นำเข้าสำเร็จ", "Import successful"),
                description: t(`นำเข้า ${extractedPrompts.length} รายการพรอมต์แล้ว`, `Imported ${extractedPrompts.length} prompts`),
            });

            // Reset input
            e.target.value = "";
        };
        reader.readAsText(file);
    };

    const handlePasteTextChange = (value: string) => {
        setPasteText(value);
        const extractedPrompts = value
            .split("\n")
            .map(line => line.trim())
            .filter(line => line.length > 0);
        const uniquePrompts = [...new Set(extractedPrompts)];
        setPrompts(uniquePrompts);
    };

    const getRequiredCredits = () => {
        const model = photoModels.find(m => m.id === selectedPhotoModel);
        return (model?.credits || 0) * (prompts.length || 0);
    };

    const handleConfirmSubmit = () => {
        if (!user || prompts.length === 0) return;

        const totalRequired = getRequiredCredits();
        if ((userCredits || 0) < totalRequired) {
            toast({
                variant: "destructive",
                title: t("เครดิตไม่พอ", "Insufficient credits"),
                description: t("กรุณาเติมเครดิตก่อนเริ่มงานแบบ Bulk", "Please buy more credits before starting bulk job"),
            });
            return;
        }

        setShowConfirmDialog(true);
    };

    const handleSubmitBulk = async () => {
        if (!user || prompts.length === 0) return;
        setShowConfirmDialog(false);

        setIsSubmitting(true);
        const modelInfo = photoModels.find(m => m.id === selectedPhotoModel);

        try {
            // 1. Create the bulk job
            const { data: job, error: jobError } = await supabase
                .from("bulk_photo_jobs" as any)
                .insert({
                    user_id: user.id,
                    model_id: selectedPhotoModel,
                    model_name: modelInfo?.name || "",
                    ratio: selectedRatio,
                    total_items: prompts.length,
                    status: 'pending'
                })
                .select()
                .single();

            if (jobError) throw jobError;

            // 2. Insert items
            const items = prompts.map(p => ({
                job_id: (job as any).id,
                user_id: user.id,
                prompt: p,
                status: 'pending'
            }));

            const { error: itemsError } = await supabase
                .from("bulk_photo_items" as any)
                .insert(items);

            if (itemsError) throw itemsError;

            // 3. Clear local state
            setPrompts([]);
            setPasteText("");

            toast({
                title: t("ส่งงานสำเร็จ", "Bulk job submitted"),
                description: t("ระบบกำลังเริ่มประมวลผล คุณสามารถปิดหน้านี้ได้ทันที", "Job is being processed. You can close this page now."),
            });

            // 4. Trigger the Edge Function immediately
            await triggerBulkProcessing();

            fetchRecentJobs();

        } catch (error: any) {
            console.error("Bulk submission error:", error);
            toast({
                variant: "destructive",
                title: t("เกิดข้อผิดพลาด", "Error occurred"),
                description: error.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClearQueue = () => {
        setPrompts([]);
        setPasteText("");
    };

    const triggerBulkProcessing = async () => {
        try {
            const session = await supabase.auth.getSession();
            const token = session.data.session?.access_token;
            if (!token) return;

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-bulk-photo`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Bulk photo processing trigger failed:", errorText);
            } else {
                const result = await response.json();
                console.log("Bulk photo processing triggered:", result);
            }
        } catch (e) {
            console.error("Bulk photo processing trigger error:", e);
        }
    };

    const handleRetryProcessing = async (jobId: string) => {
        toast({
            title: t("กำลังเริ่มประมวลผลใหม่...", "Retrying processing..."),
        });
        await triggerBulkProcessing();
        setTimeout(() => fetchRecentJobs(), 2000);
    };

    const handleDownloadAll = async (jobId: string) => {
        setDownloadingJobId(jobId);
        try {
            const { data, error } = await supabase
                .from("bulk_photo_items" as any)
                .select("log_id")
                .eq("job_id", jobId)
                .eq("status", "completed");

            if (error) throw error;
            if (!data || data.length === 0) {
                toast({
                    title: t("ไม่พบไฟล์เสร็จสมบูรณ์", "No completed files found"),
                    description: t("ยังไม่มีงานที่เสร็จสมบูรณ์ในชุดนี้", "There are no completed items in this batch yet."),
                });
                return;
            }

            const logIds = (data as any[]).map(item => item.log_id).filter(Boolean);

            const { data: logs, error: logsError } = await supabase
                .from("generation_logs")
                .select("result_url, model, prompt")
                .in("id", logIds as any);

            if (logsError) throw logsError;

            const urls = logs?.map(log => log.result_url).filter(url => url && url.startsWith("http")) || [];

            if (urls.length === 0) {
                toast({
                    title: t("ไม่พบลิงก์ดาวน์โหลด", "No download links found"),
                });
                return;
            }

            toast({
                title: t(`กำลังเริ่มดาวน์โหลด ${urls.length} ไฟล์`, `Starting download of ${urls.length} files`),
                description: t("หากเบราว์เซอร์ถาม ให้กดยืนยันเพื่อดาวน์โหลดหลายไฟล์พร้อมกัน", "If your browser asks, please allow multiple downloads."),
            });

            for (let i = 0; i < urls.length; i++) {
                const url = urls[i];
                const link = document.createElement("a");
                link.href = url;
                link.download = `photo_${jobId.substring(0, 5)}_${i + 1}.png`;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                if (i < urls.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

        } catch (error: any) {
            console.error("Download error:", error);
            toast({
                variant: "destructive",
                title: t("ดาวน์โหลดผิดพลาด", "Download failed"),
                description: error.message,
            });
        } finally {
            setDownloadingJobId(null);
        }
    };

    const selectedModelInfo = photoModels.find(m => m.id === selectedPhotoModel);

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <main className="container mx-auto px-4 py-8 mt-16 md:mt-20">
                <div className="flex flex-col gap-6 max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
                                <Image className="h-8 w-8 text-primary" />
                                {t("Bulk Photo Generation", "Bulk Photo Generation")}
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                {t("ส่งงานสร้างรูปภาพแบบ Bulk แล้วปิดหน้าเว็บได้เลย ระบบจะทยอยทำให้อัตโนมัติ", "Submit bulk photo jobs and close the page. System processes them automatically.")}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" asChild>
                                <Link to="/history">
                                    <HistoryIcon className="h-4 w-4 mr-2" />
                                    {t("ประวัติทั้งหมด", "Full History")}
                                </Link>
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left: Configuration */}
                        <div className="lg:col-span-1 space-y-6">
                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg">{t("ตั้งค่ารุ่นและขนาด", "Model & Settings")}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("เลือกรุ่น (Model)", "Select Model")}</label>
                                        <Select value={selectedPhotoModel} onValueChange={setSelectedPhotoModel}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {photoModels.map(model => (
                                                    <SelectItem key={model.id} value={model.id}>
                                                        <div className="flex justify-between w-full gap-4">
                                                            <span>{model.name}</span>
                                                            <span className="text-xs text-muted-foreground">{model.credits} Cr.</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("สัดส่วนภาพ (Aspect Ratio)", "Aspect Ratio")}</label>
                                        <Select value={selectedRatio} onValueChange={setSelectedRatio}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {aspectRatioOptions.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg">{t("นำเข้าข้อมูล", "Import Data")}</CardTitle>
                                    <CardDescription className="text-xs">
                                        {t("รองรับไฟล์ .txt หรือ .csv (1 พรอมต์ต่อ 1 บรรทัด)", "Supports .txt or .csv files (1 prompt per line)")}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Textarea for pasting prompts */}
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("วางพรอมต์ (1 บรรทัดต่อ 1 รายการ)", "Paste prompts (1 per line)")}</label>
                                        <Textarea
                                            placeholder={t("วางพรอมต์ที่นี่...\nบรรทัดที่ 1\nบรรทัดที่ 2\nบรรทัดที่ 3", "Paste prompts here...\nLine 1\nLine 2\nLine 3")}
                                            value={pasteText}
                                            onChange={(e) => handlePasteTextChange(e.target.value)}
                                            className="min-h-[150px] text-sm resize-none"
                                        />
                                    </div>

                                    {/* Divider */}
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 h-px bg-border" />
                                        <span className="text-xs text-muted-foreground">{t("หรืออัปโหลดไฟล์", "or upload file")}</span>
                                        <div className="flex-1 h-px bg-border" />
                                    </div>

                                    {/* File upload */}
                                    <div className="relative group h-24">
                                        <Input
                                            type="file"
                                            accept=".txt,.csv"
                                            onChange={handleFileUpload}
                                            className="absolute inset-0 opacity-0 cursor-pointer z-10 h-full w-full"
                                        />
                                        <div className="absolute inset-0 border-2 border-dashed border-border group-hover:border-primary/50 transition-colors rounded-lg flex flex-col items-center justify-center text-muted-foreground bg-muted/5 pointer-events-none">
                                            <Upload className="h-6 w-6 mb-1" />
                                            <p className="text-xs text-center px-4">{t("คลิกเพื่ออัปโหลดไฟล์", "Click to upload file")}</p>
                                            <p className="text-[10px] text-center px-4 mt-0.5 opacity-60">.txt, .csv</p>
                                        </div>
                                    </div>

                                    {prompts.length > 0 && (
                                        <div className="space-y-4 pt-2">
                                            {/* Summary Header */}
                                            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                                                <div className="flex justify-between text-xs text-muted-foreground uppercase font-bold tracking-wider">
                                                    <span>{t("เตรียมส่ง", "Ready to submit")}</span>
                                                    <span>{prompts.length} {t("รายการ", "items")}</span>
                                                </div>
                                                <div className="flex justify-between items-end">
                                                    <div className="text-sm">
                                                        <p className="text-xs text-muted-foreground">{t("ใช้เครดิตทั้งหมด", "Total credits needed")}:</p>
                                                        <p className="font-bold text-lg text-primary">{getRequiredCredits().toLocaleString()} Cr.</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-[10px] text-muted-foreground">{t("โมเดล", "Model")}</p>
                                                        <p className="text-xs font-medium text-primary">{selectedModelInfo?.name} ({selectedModelInfo?.credits} Cr./ภาพ)</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Preview List */}
                                            <div className="border border-border/50 rounded-lg overflow-hidden">
                                                <div className="bg-muted/30 px-3 py-2 border-b border-border/50">
                                                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                                        {t("รายการพรอมต์ทั้งหมด", "All Prompts Preview")}
                                                    </p>
                                                </div>
                                                <div className="max-h-[250px] overflow-y-auto">
                                                    {prompts.map((prompt, index) => (
                                                        <div
                                                            key={index}
                                                            className="px-3 py-2 border-b border-border/30 last:border-b-0 hover:bg-muted/20 transition-colors"
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <span className="text-[10px] font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 shrink-0">
                                                                    {index + 1}
                                                                </span>
                                                                <p className="text-xs text-foreground/80 line-clamp-2 break-all">
                                                                    {prompt}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <Button
                                                className="w-full h-12 gap-2 text-lg shadow-glow"
                                                variant="glow"
                                                onClick={handleConfirmSubmit}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Eye className="h-5 w-5" />}
                                                {t("ตรวจสอบ & ยืนยันส่งงาน", "Review & Confirm")}
                                            </Button>

                                            <Button variant="ghost" size="sm" className="w-full text-destructive" onClick={handleClearQueue}>
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                {t("ล้างรายการ", "Clear items")}
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right: History & Progress */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card variant="glass" className="min-h-[500px]">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Clock className="h-5 w-5 text-primary" />
                                        {t("สถานะงานปัจจุบัน", "Active Bulk Jobs")}
                                    </CardTitle>
                                    <CardDescription>
                                        {t("งานจะทยอยเสร็จและไปอยู่ในหน้าประวัติหลัก", "Jobs process over time and results appear in History")}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {recentJobs.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground opacity-50">
                                                <FileText className="h-16 w-16 mb-4" />
                                                <p>{t("ยังไม่มีรายการส่งงาน", "No recent bulk jobs")}</p>
                                            </div>
                                        ) : (
                                            recentJobs.map((job) => (
                                                <div key={job.id} className="p-4 rounded-xl border border-border bg-card/50 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-bold text-sm">{job.model_name}</p>
                                                                <Badge variant="outline" className="text-[10px] py-0">{job.ratio}</Badge>
                                                            </div>
                                                            <p className="text-[10px] text-muted-foreground">{new Date(job.created_at).toLocaleString()}</p>
                                                        </div>
                                                        <Badge variant={job.status === 'completed' ? 'secondary' : (job.status === 'processing' ? 'secondary' : 'outline')} className={job.status === 'completed' ? 'bg-green-500/10 text-green-500 border-green-500/20' : ''}>
                                                            {job.status === 'processing' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                                                            {job.status}
                                                        </Badge>
                                                    </div>

                                                    <div className="space-y-1">
                                                        <div className="flex justify-between text-xs mb-1">
                                                            <span>{t("ความคืบหน้า", "Progress")}</span>
                                                            <span className="font-bold">
                                                                {job.completed_items + job.failed_items} / {job.total_items}
                                                            </span>
                                                        </div>
                                                        <Progress value={((job.completed_items + job.failed_items) / job.total_items) * 100} className="h-1.5" />
                                                    </div>

                                                    <div className="flex justify-end gap-2 pt-1">
                                                        {(job.status === 'pending' || job.status === 'processing') && job.completed_items + job.failed_items < job.total_items && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 text-xs gap-2"
                                                                onClick={() => handleRetryProcessing(job.id)}
                                                            >
                                                                <RefreshCw className="h-3.5 w-3.5" />
                                                                {t("เริ่มประมวลผล", "Start Processing")}
                                                            </Button>
                                                        )}
                                                        {job.completed_items > 0 && (
                                                            <Button
                                                                size="sm"
                                                                variant="glow"
                                                                className="h-8 text-xs gap-2"
                                                                onClick={() => handleDownloadAll(job.id)}
                                                                disabled={downloadingJobId === job.id}
                                                            >
                                                                {downloadingJobId === job.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                                                {t("ดาวน์โหลดทั้งหมด", "Download All")} ({job.completed_items})
                                                            </Button>
                                                        )}
                                                        <Button size="sm" variant="outline" className="h-8 text-xs" asChild>
                                                            <Link to="/history">
                                                                <HistoryIcon className="h-3.5 w-3.5 mr-2" />
                                                                {t("ดูในประวัติ", "View in History")}
                                                            </Link>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Photo History Section */}
                    {recentPhotos.length > 0 && (
                        <div className="mt-8">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <Image className="h-5 w-5 text-primary" />
                                    {t("รูปภาพล่าสุด", "Recent Photos")}
                                </h2>
                                <Button variant="outline" size="sm" asChild>
                                    <Link to="/history/photo">
                                        {t("ดูทั้งหมด", "View All")}
                                    </Link>
                                </Button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                                {recentPhotos.map((photo) => {
                                    let photoUrl = photo.result_url;
                                    if (photoUrl?.startsWith('{')) {
                                        try {
                                            const parsed = JSON.parse(photoUrl);
                                            photoUrl = parsed.resultUrls?.[0] || parsed.url || photoUrl;
                                        } catch { }
                                    } else if (photoUrl?.includes(',')) {
                                        photoUrl = photoUrl.split(',')[0];
                                    }

                                    return (
                                        <Card key={photo.id} variant="glass" className="overflow-hidden group">
                                            <div className="relative bg-black/5 dark:bg-white/5">
                                                <img
                                                    src={photoUrl || ""}
                                                    alt={photo.prompt}
                                                    className="w-full aspect-square object-cover"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <CardContent className="p-3">
                                                <p className="text-xs text-muted-foreground line-clamp-2 mb-2" title={photo.prompt}>
                                                    {photo.prompt}
                                                </p>
                                                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                                    <span>{photo.model}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-primary">-{photo.credits_used} Cr.</span>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => photoUrl && window.open(photoUrl, "_blank")}
                                                        >
                                                            <Download className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-primary" />
                            {t("ยืนยันการส่งงานแบบ Bulk", "Confirm Bulk Submission")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("กรุณาตรวจสอบข้อมูลก่อนยืนยัน", "Please review the details before confirming")}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg bg-muted/30 border border-border">
                                <p className="text-xs text-muted-foreground">{t("โมเดล", "Model")}</p>
                                <p className="font-bold text-sm">{selectedModelInfo?.name}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/30 border border-border">
                                <p className="text-xs text-muted-foreground">{t("สัดส่วน", "Ratio")}</p>
                                <p className="font-bold text-sm">{selectedRatio}</p>
                            </div>
                        </div>

                        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm">{t("จำนวนรูปภาพ", "Number of photos")}</span>
                                <span className="font-bold text-lg">{prompts.length} {t("ภาพ", "photos")}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm">{t("เครดิตต่อภาพ", "Credits per photo")}</span>
                                <span className="font-bold">{selectedModelInfo?.credits} Cr.</span>
                            </div>
                            <div className="h-px bg-border" />
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-bold">{t("เครดิตรวมทั้งหมด", "Total credits")}</span>
                                <span className="font-bold text-xl text-primary">{getRequiredCredits().toLocaleString()} Cr.</span>
                            </div>
                            <div className="flex justify-between items-center text-xs text-muted-foreground">
                                <span>{t("เครดิตคงเหลือ", "Remaining credits")}</span>
                                <span className={(userCredits || 0) < getRequiredCredits() ? "text-destructive" : "text-green-500"}>
                                    {(userCredits || 0).toLocaleString()} Cr.
                                </span>
                            </div>
                        </div>

                        {(userCredits || 0) < getRequiredCredits() && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                                <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                                <p className="text-xs text-destructive">
                                    {t("เครดิตไม่เพียงพอ กรุณาเติมเครดิตก่อนส่งงาน", "Insufficient credits. Please top up before submitting.")}
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            {t("ยกเลิก", "Cancel")}
                        </Button>
                        <Button
                            variant="glow"
                            onClick={handleSubmitBulk}
                            disabled={isSubmitting || (userCredits || 0) < getRequiredCredits()}
                            className="gap-2"
                        >
                            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            {t("ยืนยันส่งงาน", "Confirm Submit")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PhotoBulk;
